import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import React, { lazy, Suspense, useRef, useMemo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import throttle from 'lodash/throttle';
import { BiData } from '@wix/da-bi/pkg/events';
import { Url } from '@wix/da-url';
import { useIsMounted } from '@wix/da-hooks/pkg/useIsMounted';
import NoteIcon from '@wix/da-ds/pkg/Icons/24x24/Mail';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import IconButtonWithNotification from '@wix/da-ds/pkg/Buttons/IconButtonWithNotification';
import ErrorBoundary from '../../../ErrorBoundary';
import { useVerification } from '../../../VerificationPopup/withVerification';
import NotesModuleLoader from '../../../Notes/redux/drawer/ModuleLoaderLazy';
import retry from '../../../utils/webpack/chunkRetry';
const DesktopNotesPanel = /*#__PURE__*/ lazy(()=>retry(()=>import(/* webpackChunkName: "notes_desktop_panel" */ './DesktopNotesPanel')));
import s from './NotesButtonAndDropdown.scss';
export const NotesButtonAndDropdown = ({ className, iconClassName, numberClassName, disableDropdown, unreadCount })=>{
    const [isOpen, setIsOpen] = useState(false);
    const { t } = useTranslation();
    const isMounted = useIsMounted();
    const { isVerificationNeeded, pushVerificationPopup } = useVerification();
    const rootRef = useRef(null);
    const enterTimerRef = useRef();
    const handleOpen = useCallback(()=>{
        setIsOpen(true);
    }, []);
    const handleClose = useCallback(()=>{
        setIsOpen(false);
    }, []);
    const throttledMouseLeave = useMemo(()=>throttle(()=>{
            clearTimeout(enterTimerRef.current);
            handleClose();
        }, 300), [
        handleClose
    ]);
    const onMouseEnter = ()=>{
        enterTimerRef.current = setTimeout(()=>{
            handleOpen();
        }, 100);
    };
    const onClosed = useCallback(()=>{
        throttledMouseLeave();
    }, [
        throttledMouseLeave
    ]);
    return /*#__PURE__*/ React.createElement("div", {
        className: classnames(s['root'], className),
        onMouseEnter: onMouseEnter,
        onMouseLeave: onClosed,
        ref: rootRef
    }, !isVerificationNeeded && /*#__PURE__*/ React.createElement(TextButton, {
        className: s['a11y-toggle'],
        size: "medium",
        variant: "main",
        "aria-haspopup": true,
        "aria-expanded": isOpen,
        "aria-controls": "site-header-notes",
        onClick: isOpen ? handleClose : handleOpen
    }, t('siteHeader.notes.open')), /*#__PURE__*/ React.createElement(IconButtonWithNotification, _object_spread({
        icon: NoteIcon,
        size: "medium",
        notificationColor: "red",
        iconButtonClassName: iconClassName,
        numberClassName: numberClassName,
        count: unreadCount,
        biData: BiData({
            evid: 700,
            link_name: 'notes',
            indicator: unreadCount
        })
    }, isVerificationNeeded ? {
        onClick: ()=>pushVerificationPopup('notes'),
        'aria-label': t('siteHeader.a11y.openAccountVerificationDialog')
    } : {
        href: Url.notesHomeLink(),
        'aria-label': t('siteHeader.a11y.notes.buttonLink')
    })), /*#__PURE__*/ React.createElement(ErrorBoundary, {
        debugComponent: "NotesButtonAndDropdown"
    }, isMounted && isOpen && !disableDropdown && /*#__PURE__*/ React.createElement(Suspense, {
        fallback: null
    }, /*#__PURE__*/ React.createElement(NotesModuleLoader, null), /*#__PURE__*/ React.createElement(DesktopNotesPanel, {
        id: "site-header-notes",
        className: classnames(s['dropdown'], isOpen && s['open'], s['a11y-dropdown']),
        onClose: onClosed
    }))));
};
NotesButtonAndDropdown.displayName = 'NotesButtonAndDropdown';
export default NotesButtonAndDropdown;
